import { useMemo, type ReactNode } from 'react'
import { Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { DetailCard } from '@/admin/components/shared/detail-card'
import { useFeatureFlags } from '@/admin/hooks/use-feature-flag'
import {
  useClaimTeam,
  useSetTeamSlot,
  type TeamSlot,
} from '@/admin/hooks/use-claim-team'
import {
  SearchableSelect,
  type SearchableSelectGroup,
  type SearchableSelectOption,
} from '@/shared/components/ui/searchable-select'
import { toastApiError } from '@/shared/lib/api-error'

/**
 * Claim Team (WPS-160 §3/§4) - the per-slot assignees of a claim shown on the
 * overview: the primary Resolution Specialist plus the backup RS, lab,
 * settlement and inspector slots. Each row reassigns its slot to another
 * member holding that slot's role (the same-role rule), writing through the
 * generic slot service.
 *
 * Entirely data-driven: the slot catalogue is tenant config, and the panel
 * only renders for tenants on the `advanced_routing` capability with a slot
 * catalogue (the backend 404s otherwise). No slug checks, no hardcoded roles.
 * Slot labels/roles are tenant data from the API; the fixed chrome copy is
 * translated (keys under `claimTeam.*` in seed_translations).
 */

const UNASSIGN = '__unassign__'

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function SlotRow({
  slot,
  onChange,
  disabled,
}: {
  slot: TeamSlot
  onChange: (userUuid: string | null) => void
  disabled: boolean
}) {
  const { t } = useTranslation()
  const groups: SearchableSelectGroup[] = useMemo(() => {
    const options: SearchableSelectOption[] = slot.assignable.map((u) => ({
      value: u.uuid,
      label: u.name,
    }))
    const assignee = slot.assignee
    if (assignee) {
      // Keep the current holder selectable/displayable even when they no longer
      // qualify as "assignable" (deactivated, role changed, or left the org) -
      // otherwise the select can't match its value against any option and falls
      // back to the "Unassigned" placeholder while the avatar still renders the
      // holder's initials (WPS-160: "card shows initials but dropdown says
      // unassigned").
      if (!slot.assignable.some((u) => u.uuid === assignee.uuid)) {
        options.unshift({ value: assignee.uuid, label: assignee.name })
      }
      options.unshift({ value: UNASSIGN, label: t('claimTeam.unassign', 'Unassign') })
    }
    return [
      {
        label: slot.role
          ? t('claimTeam.roleTeam', '{{role}} team', { role: slot.role })
          : t('claimTeam.assignable', 'Assignable'),
        options,
      },
    ]
  }, [slot.assignable, slot.assignee, slot.role, t])

  return (
    <div
      data-testid={`claim-team-slot-${slot.key}`}
      className="grid grid-cols-[1fr_minmax(0,220px)] items-center gap-3 border-b-[0.5px] border-[var(--rule-light)] px-4 py-3 last:border-b-0"
    >
      <div className="flex min-w-0 items-center gap-2.5">
        {slot.assignee ? (
          <span className="grid size-7 shrink-0 place-items-center rounded-full bg-[var(--accent-soft,var(--bg-subtle))] text-[11px] font-medium text-[var(--ink-2)]">
            {initials(slot.assignee.name)}
          </span>
        ) : (
          <span className="grid size-7 shrink-0 place-items-center rounded-full bg-[var(--bg-subtle)] text-[12px] text-[var(--ink-3)]">
            +
          </span>
        )}
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-[13px] font-medium text-[var(--ink)]">
            {slot.label}
          </span>
          <span className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
            {slot.primary ? t('claimTeam.primary', 'Primary') : slot.key}
          </span>
        </div>
      </div>
      <SearchableSelect
        value={slot.assignee?.uuid ?? ''}
        onValueChange={(v) => onChange(v === UNASSIGN ? null : v)}
        groups={groups}
        placeholder={t('claimTeam.unassigned', 'Unassigned')}
        disabled={disabled}
      />
    </div>
  )
}

export function ClaimTeamPanel({
  claimUuid,
  footer,
}: {
  claimUuid: string
  /**
   * Optional control rendered inside the card beneath the slots - used to fold
   * "assign to a partner" into the single assignment surface (WPS-160), since a
   * partner assignment is capability-based (it forks a partner workflow) rather
   * than a same-role slot write.
   */
  footer?: ReactNode
}) {
  const { t } = useTranslation()
  const flags = useFeatureFlags()
  const enabled = flags['advanced_routing'] === true

  const { data, isPending } = useClaimTeam(claimUuid, enabled)
  const setSlot = useSetTeamSlot(claimUuid)

  if (!enabled) return null

  // Loading first, so a tenant with a team never flashes an empty/absent panel.
  if (isPending) {
    return (
      <DetailCard
        title={t('claimTeam.title', 'Claim Team')}
        icon={<Users className="h-3.5 w-3.5" />}
        collapsible={false}
      >
        <div className="px-4 py-6 text-[13px] text-[var(--ink-3)]">
          {t('claimTeam.loading', 'Loading team…')}
        </div>
      </DetailCard>
    )
  }

  const slots = data?.slots ?? []
  if (slots.length === 0) return null

  const handleChange = (slotKey: string, userUuid: string | null) => {
    setSlot.mutate(
      { slotKey, userUuid },
      {
        onError: (err) =>
          toastApiError(err, t('claimTeam.updateFailed', 'Failed to update the claim team')),
      },
    )
  }

  return (
    <DetailCard
      title={t('claimTeam.title', 'Claim Team')}
      icon={<Users className="h-3.5 w-3.5" />}
      collapsible={false}
    >
      {slots.map((slot) => (
        <SlotRow
          key={slot.key}
          slot={slot}
          disabled={setSlot.isPending}
          onChange={(userUuid) => handleChange(slot.key, userUuid)}
        />
      ))}
      {footer ? (
        <div className="border-t-[0.5px] border-[var(--rule-light)] px-4 py-3">
          {footer}
        </div>
      ) : null}
    </DetailCard>
  )
}
